import React, { useEffect } from "react";
import Layout from "../../../components/Layout";
import { Link } from "react-router-dom";
import { FilterContextProvider } from "../../../contexts/FilterContext";
import CreateOrderForm from "../../../components/order/Create";
import Chip from "../../../components/Chip";
import MessagePopup from "../../../components/MessagePopup";
import DataTable from "../../../components/DataTable";
import useData from "../../../hooks/useData";
import FilterOrders from "../../../api/orders/filterOrders";
import { EyeOpen } from "../../../assets/icons/Icons";
import SearchFilter from "../../../components/filters/SearchFilter";
import ViewOrder from "../../../components/order/View";
import DateRangeFilter from "../../../components/filters/DateRangeFilter";
import { formatDate } from "../../../utils/formatDate";
import { useUserContext } from "../../../contexts/UserContext";
import ResetFilters from "../../../components/filters/ResetFilters";
import { useTranslation } from "react-i18next";
import CustomTooltip from "../../../components/CustomTooltip";
import { useGlobalContext } from "../../../contexts/GlobalContext";

function Index(props) {
  const { t } = useTranslation();

  const {
    data,
    errorMessage,
    loadingData,
    pageCount,
    selectedItem,
    setSelectedItem,
    newItemAdded,
    setNewItemAdded,
    showCreateItem,
    setShowCreateItem,
    showViewItem,
    setShowViewItem,
  } = useData(FilterOrders);

  const { can: userCan, hasPermission, hasDynamicPermissions } =
    useUserContext();

  const canCreate = hasDynamicPermissions()
    ? hasPermission("card_orders", "write")
    : userCan("order", "write");

  const { setPageTitle, setBreadcrumbs } = useGlobalContext();

  useEffect(() => {
    setPageTitle(t("Card Issuing"));
    setBreadcrumbs([t("Card Issuing"), t("Orders")]);

    return () => {
      setPageTitle("");
      setBreadcrumbs([]);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <Layout title={t("Orders")} breadcrumbs={t("Card Issuing")}>
      {showCreateItem && (
        <CreateOrderForm
          onClose={() => setShowCreateItem(false)}
          onCreate={(e) => {
            setShowCreateItem(false);
            setNewItemAdded(true);
          }}
        />
      )}
      {newItemAdded && (
        <MessagePopup
          title={t("Order Created!")}
          message={t("The order has been successfully created!")}
          onClose={(e) => setNewItemAdded(false)}
        />
      )}
      {showViewItem && (
        <ViewOrder
          order={selectedItem}
          onClose={() => setShowViewItem(false)}
        />
      )}

      <DataTable
        filtersTop={
          <FilterContextProvider name="s">
            <SearchFilter />
          </FilterContextProvider>
        }
        filtersBottom={
          <>
            <FilterContextProvider name="date">
              <DateRangeFilter />
            </FilterContextProvider>
            <FilterContextProvider name="id">
              <SearchFilter
                hideIcon
                placeholderText="ID"
                inputStyles={{ maxWidth: "140px" }}
              />
            </FilterContextProvider>
            <FilterContextProvider name="office_id">
              <SearchFilter
                hideIcon
                placeholderText={t("Office ID")}
                inputStyles={{ maxWidth: "140px" }}
              />
            </FilterContextProvider>
            <FilterContextProvider>
              <ResetFilters />
            </FilterContextProvider>
          </>
        }
        buttons={
          <>
            {canCreate && (
              <button
                className="btn btn-primary btn-medium"
                onClick={(e) => setShowCreateItem(true)}
              >
                {t("Create")}
              </button>
            )}
          </>
        }
        columns={[
          { header: t("Order ID"), accessor: "id" },
          { header: t("Order Date"), accessor: "created_at" },
          { header: t("Office ID"), accessor: "office_id" },
          { header: t("Created by"), accessor: "created_by" },
          { header: t("Package Code"), accessor: "package_code" },
          { header: t("Shipping ID"), accessor: "shipping_address_id" },
          { header: t("Card Count"), accessor: "quantity" },
          { header: t("Status"), accessor: "status" },
          { header: t("Action"), accessor: "actions" },
        ]}
        rows={data.data || []}
        pageCount={pageCount}
        loadingData={loadingData}
        error={errorMessage}
        actions={(item) => (
          <div className="d-flex align-items-center">
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedItem(item);
                setShowViewItem(true);
              }}
            >
              <CustomTooltip icon={<EyeOpen />} message={t("View")} />
            </Link>
          </div>
        )}
        customOutputs={(columnName, value) => {
          switch (columnName) {
            case "status":
              let status = "inactive";
              if (value === "submitted") {
                status = "active";
              }
              return (
                <Chip
                  state={status}
                  label={status.charAt(0).toUpperCase() + status.slice(1)}
                  showAsTooltop
                />
              );
            case "created_at":
              return formatDate(value);
            default:
              return value;
          }
        }}
      />
    </Layout>
  );
}

export default Index;
